import * as React from 'react';
import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl';

import {
  IconRegularBadge,
  IconRegularDislike,
  IconRegularGift,
  IconRegularLike,
  IconSolidDislike,
  IconSolidLike,
  Ratings,
  Text,
} from '@notino/react-styleguide';
import { AuthorType, GetReviewsQuery } from '@notino/shared/definitions/types';

import { Flex } from '@components/Flex';
import { PortalTooltip } from '@components/PortalTooltip';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useApolloClientHeaders } from '@hooks/useApolloClientHeaders';
import { useTranslateOrNull } from '@hooks/useTranslateOrNull';
import { ProsAndCons } from '@lib/reviews/ProsAndCons';
import { useReviewPhotoModal } from '@lib/reviews/ReviewPhotoModal';
import { useGetReviewLabelComponents } from '@lib/reviews/useGetReviewLabelComponents';
import { ExpandableText } from '@sharedComponents/ExpandableText';
import { getRelativeTimeProps } from '@utils/date';

import pdMessages from '../../../../../../messages';
import { messages as reviewMessages } from '../../../messages';
import { MoreInfoLink, TooltipContent } from '../ReviewMotivation/styled';

import { isReviewTextExpandable } from './isReviewTextExpandable';
import {
  ImagesWrapper,
  ReviewContent,
  ReviewStats,
  ReviewText,
  SecondaryText,
  ThumbsUpDownWrapper,
  TitleAndVariantAndTextWrapper,
  TitleText,
  Username,
  Wrapper,
} from './styled';
import { VariantDisplay } from './VariantDisplay';

type Props = {
  item: NonNullable<
    NonNullable<GetReviewsQuery['reviewsAndFilters']>['data']
  >['reviews'][number];
  starsId: number | string;
  like: () => Promise<void>;
  dislike: () => Promise<void>;
};

export const ReviewItemDisplay = ({ item, starsId, like, dislike }: Props) => {
  const { formatMessage } = useIntl();
  const { shopId } = useApolloClientHeaders();
  const { openReviewPhotoModal } = useReviewPhotoModal();
  const getLabels = useGetReviewLabelComponents();
  const { product } = useProductDetailContext();
  const translate = useTranslateOrNull();
  const [showOriginal, setShowOriginal] = React.useState(false);

  const toggleShowOriginal = () => setShowOriginal(!showOriginal);
  const displayText =
    showOriginal && item.originalText ? item.originalText : item.text;
  const displayTitle =
    showOriginal && item.originalTitle ? item.originalTitle : item.title;

  const isVerified =
    item.authorType === AuthorType.Verified ||
    item.authorType === AuthorType.VerifiedPartner;
  const isMotivated = item.authorType === AuthorType.Motivated;

  const moreLinkMessage =
    reviewMessages[`review${String(item.authorType)}MoreLink`];
  const moreInfoLink = moreLinkMessage ? translate(moreLinkMessage.id) : null;

  const getUserNameOrCommentInfo = () => {
    if (item.isAnonymized) {
      const message =
        pdMessages[
          item.originalShopId ? 'anonymizedSharedComment' : 'anonymizedComment'
        ];

      return (
        <FormattedMessage
          {...message}
          values={{
            originalShopId: item.originalShopId ?? shopId,
          }}
        />
      );
    }

    return <span style={{ wordBreak: 'break-all' }}>{item.userName}</span>;
  };

  const getMoreLinkComponent = () => {
    return (
      moreInfoLink && (
        <MoreInfoLink href={moreInfoLink} rel="nofollow" target="__blank">
          {formatMessage(reviewMessages.reviewMoreText)}
        </MoreInfoLink>
      )
    );
  };

  const labels = getLabels(item, showOriginal, toggleShowOriginal);
  const showVariant =
    !!item.variantId &&
    (product.variants.length > 1 ||
      product.variants[0].catalogId !== String(item.variantId));

  return (
    <Wrapper>
      <ReviewStats>
        <Flex direction="column" gap="8px">
          <Ratings
            ratingId={`reviewItem${starsId}`}
            size={14}
            rating={item.score}
            background="icon.disabled"
            foreground="icon.primary"
          />
          <Username data-testid="user-name-or-comment-info">
            {getUserNameOrCommentInfo()}
          </Username>

          {(isVerified || isMotivated) && (
            <Flex gap="2px">
              <PortalTooltip
                interactive={true}
                content={
                  <PortalTooltip.Content>
                    <TooltipContent>
                      <FormattedMessage
                        {...reviewMessages[
                          `review${item.authorType as string}Description`
                        ]}
                      />

                      {getMoreLinkComponent()}
                    </TooltipContent>
                  </PortalTooltip.Content>
                }
              >
                {isVerified ? (
                  <IconRegularBadge
                    width="16px"
                    height="16px"
                    color="icon.positive"
                  />
                ) : (
                  <IconRegularGift
                    width="16px"
                    height="16px"
                    color="icon.tertiary"
                  />
                )}
              </PortalTooltip>
              <Text
                typography="labelSmall"
                color={isMotivated ? 'text.tertiary' : 'text.positive'}
              >
                <FormattedMessage
                  {...reviewMessages[`review${item.authorType as string}Title`]}
                />
              </Text>
            </Flex>
          )}
        </Flex>

        {/*
          `suppressHydrationWarning`: this leaf is volatile by design — the
          relative time is a function of when the render happens, so the SSR
          pass and hydration can legitimately disagree ("2 hours ago" vs
          "3 hours ago"). Only the warning is suppressed; React still re-renders
          the text to the client-correct value. See US #987145.
        */}
        <SecondaryText suppressHydrationWarning={true}>
          <FormattedRelativeTime {...getRelativeTimeProps(item.createdDate)} />
        </SecondaryText>
      </ReviewStats>

      <ReviewContent>
        {(item.title || showVariant || item.text || item.attributes) && (
          <TitleAndVariantAndTextWrapper>
            {displayTitle && <TitleText>{displayTitle}</TitleText>}

            {showVariant && (
              <VariantDisplay catalogId={String(item.variantId)} />
            )}

            {(displayText || item.attributes) && (
              <div>
                <ExpandableText
                  disabled={!isReviewTextExpandable(item)}
                  height="170px"
                  buttonText={formatMessage(pdMessages.showMoreText)}
                >
                  <Flex
                    direction="column"
                    gap="1.5rem"
                    style={{
                      marginTop: item.variantId && !displayText ? '1rem' : 0,
                    }}
                  >
                    {displayText && <ReviewText>{displayText}</ReviewText>}
                    {item.attributes && (
                      <ProsAndCons attributes={item.attributes} />
                    )}
                  </Flex>
                </ExpandableText>
              </div>
            )}
          </TitleAndVariantAndTextWrapper>
        )}

        {(item.images?.length ?? 0) > 0 && (
          <ImagesWrapper>
            {item.images?.map((image, index) => (
              <button
                key={image.thumbnailUrl}
                type="button"
                onClick={() =>
                  openReviewPhotoModal({
                    reviewId: item.id,
                    initialImage: image,
                  })
                }
              >
                <img
                  src={image.thumbnailUrl ?? undefined}
                  alt={`Review ${index + 1}`}
                />
              </button>
            ))}
          </ImagesWrapper>
        )}

        {labels.length > 0 && (
          <Flex direction="column" gap="12px">
            {labels}
          </Flex>
        )}

        {(item.text || item.title || (item.images?.length ?? 0) > 0) && (
          <Flex margin="auto 0 0 0">
            <SecondaryText>
              <FormattedMessage {...pdMessages.reviewWasItUsefull} />
            </SecondaryText>

            <ThumbsUpDownWrapper>
              <button type="button" onClick={like}>
                {item.alreadyLiked ? <IconSolidLike /> : <IconRegularLike />}
                <SecondaryText>{item.like}</SecondaryText>
              </button>

              <button type="button" onClick={dislike}>
                {item.alreadyDisliked ? (
                  <IconSolidDislike />
                ) : (
                  <IconRegularDislike />
                )}
                <SecondaryText>{item.dislike}</SecondaryText>
              </button>
            </ThumbsUpDownWrapper>
          </Flex>
        )}
      </ReviewContent>
    </Wrapper>
  );
};
